import { Injectable } from '@angular/core';

import { Action, select, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { CategoriesService } from '~app/modules/generic/services/categories.service';
import {
  CategoryActionTypes,
  LoadCategory,
  LoadCategorySuccess,
  LoadCategoryFailure,
  ClearCategory
} from './category.actions';
import * as fromRouter from '~core/store';
import { getCategories } from '~app/modules/generic/store/categories/categories.selectors';
import { getCategorySlug } from './category.selectors';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { Category } from '../../models';
import { RouterStateUrl } from '~core/store';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Injectable()
export class CategoryEffects {
  selectedRegion: string;
  private activeRoute$ = this.store$.pipe(select(fromRouter.getRouterState));
  userRegion$: Observable<string> = this.regionSelectorService.userRegion$.pipe(
    filter((value) => value !== undefined),
    tap((value) => (this.selectedRegion = value))
  );

  constructor(
    private actions$: Actions,
    private store$: Store<fromRouter.RouterState>,
    private categoriesService: CategoriesService,
    private productsListUrlService: ProductsListUrlService,
    private readonly regionSelectorService: RegionSelectorService
  ) {
  }

  @Effect()
  loadCategory$: Observable<Action> = this.actions$.pipe(
    ofType(CategoryActionTypes.LOAD_CATEGORY),
    switchMap(({ payload: { slug } }) => {
      return this.categoriesService.getCategoryByIdOrSlug(slug)
        .pipe(
          map(category => new LoadCategorySuccess(category)),
          catchError(error => of(new LoadCategoryFailure({ error })))
        );
    })
  );

  @Effect()
  loadCategoryByRouteOrRegion$: Observable<Action> = combineLatest([
    this.activeRoute$,
    this.store$.pipe(select(getCategories)),
    this.store$.pipe(select(getCategorySlug)),
    this.userRegion$,
  ]).pipe(
    filter(([
              routeState,
              categories,
              savedCategorySlug
            ]) => {

        return this.canLoadCategories(routeState, categories, savedCategorySlug);
      }
    ),
    switchMap((_) => {
      return of(new LoadCategory({ slug: this.productsListUrlService.getCategory() }));
    })
  );

  @Effect()
  clearCategoryByRoute$: Observable<Action> = this.activeRoute$.pipe(
    filter((routeState) => {

        if (!(routeState && routeState.url)) {
          return false;
        }

        if (
          !this.productsListUrlService.isCategoryPage()
          && !this.productsListUrlService.isCustomPLPPage()
          && !this.productsListUrlService.isSearchPage()
        ) {
          return true;
        }

        return this.productsListUrlService.getCategory() == null;
      }
    ),
    switchMap((_) => {
      return of(new ClearCategory());
    })
  );

  private canLoadCategories(
    routeState: RouterStateUrl,
    categories: Category[],
    savedCategorySlug: string
  ): boolean {
    if (
      !(routeState && routeState.url)
      || !categories
      || !categories.length
    ) {
      return false;
    }

    if (
      !this.productsListUrlService.isCategoryPage()
      && !this.productsListUrlService.isCustomPLPPage()
      && !this.productsListUrlService.isSearchPage()
    ) {
      return false;
    }

    const currentCategorySlug = this.productsListUrlService.getCategory();
    const savedRegionSelection: string = this.regionSelectorService.getUserRegion()

    return !!savedRegionSelection || !(!currentCategorySlug || savedCategorySlug === currentCategorySlug);
  }
}
